import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { useEffect } from "react";
import { Footer } from "@/components/utilities/Footer";
import { useTheme } from "@/contexts/ThemeContext";

const TermsOfUse = () => {
  const { theme } = useTheme();
  
  // Force scroll to top on component mount with a more reliable approach
  useEffect(() => {
    // Use both approaches for maximum compatibility
    window.scrollTo(0, 0);
    // Also try this alternative method
    document.body.scrollTop = 0; // For Safari
    document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Opera
    
    // Do this with a slight delay to ensure it happens after any layout calculations
    const timer = setTimeout(() => {
      window.scrollTo(0, 0);
      document.body.scrollTop = 0;
      document.documentElement.scrollTop = 0;
    }, 100);
    
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className={`min-h-screen flex flex-col ${theme === 'dark' ? 'bg-[#030405]' : 'bg-background'}`}>
      <div className="container mx-auto px-4 py-16 flex-grow">
        <div className="mb-8">
          <Button
            variant="ghost"
            className={`flex items-center ${theme === 'dark' ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
            asChild
          >
            <Link to="/">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Home
            </Link>
          </Button>
        </div>
        
        <div className={`max-w-4xl mx-auto ${theme === 'dark' ? 'bg-[#050607]/70' : 'bg-white/80'} rounded-lg p-8 shadow-lg border ${theme === 'dark' ? 'border-gray-800/50' : 'border-gray-200'}`}>
          <h1 className={`text-3xl font-bold mb-6 ${theme === 'dark' ? 'text-white' : 'text-gray-800'}`}>Terms of Use</h1>
          
          <div className={`prose ${theme === 'dark' ? 'prose-invert' : ''} max-w-none`}>
            <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'} mb-4`}>Last Updated: {new Date().toLocaleDateString()}</p>
            
            <h2 className={`text-xl font-semibold mt-6 mb-3 ${theme === 'dark' ? 'text-white' : 'text-gray-800'}`}>1. Agreement to Terms</h2>
            <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'} mb-4`}>
              By accessing or using DocGen, you agree to be bound by these Terms of Use and all applicable laws and regulations. If you do not agree with any of these terms, you are prohibited from using or accessing this site.
            </p>
            
            <h2 className={`text-xl font-semibold mt-6 mb-3 ${theme === 'dark' ? 'text-white' : 'text-gray-800'}`}>2. Use License</h2>
            <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'} mb-4`}>
              Permission is granted to temporarily access the materials on DocGen's website for personal, non-commercial transitory viewing only. This is the grant of a license, not a transfer of title, and under this license you may not:
            </p>
            <ul className={`list-disc pl-6 mb-4 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
              <li>Modify or copy the materials</li>
              <li>Use the materials for any commercial purpose</li>
              <li>Attempt to decompile or reverse engineer any software contained on DocGen's website</li>
              <li>Remove any copyright or other proprietary notations from the materials</li>
              <li>Transfer the materials to another person or "mirror" the materials on any other server</li>
            </ul>
            
            <h2 className={`text-xl font-semibold mt-6 mb-3 ${theme === 'dark' ? 'text-white' : 'text-gray-800'}`}>3. Account Terms</h2>
            <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'} mb-4`}>
              To access certain features of DocGen, you may be required to register for an account. You must provide accurate, current, and complete information during the registration process. You are responsible for safeguarding your account credentials and for any activities or actions under your account.
            </p>
            
            <h2 className={`text-xl font-semibold mt-6 mb-3 ${theme === 'dark' ? 'text-white' : 'text-gray-800'}`}>4. User Content</h2>
            <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'} mb-4`}>
              You retain ownership of any intellectual property rights that you hold in content you submit to DocGen. By submitting content to DocGen, you grant us a worldwide, royalty-free license to use, reproduce, modify, adapt, publish, translate, distribute, and display such content.
            </p>
            
            <h2 className={`text-xl font-semibold mt-6 mb-3 ${theme === 'dark' ? 'text-white' : 'text-gray-800'}`}>5. Limitation of Liability</h2>
            <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'} mb-4`}>
              In no event shall DocGen or its suppliers be liable for any damages (including, without limitation, damages for loss of data or profit, or due to business interruption) arising out of the use or inability to use the materials on DocGen's website, even if DocGen or a DocGen authorized representative has been notified orally or in writing of the possibility of such damage.
            </p>
            
            <h2 className={`text-xl font-semibold mt-6 mb-3 ${theme === 'dark' ? 'text-white' : 'text-gray-800'}`}>6. Accuracy of Materials</h2>
            <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'} mb-4`}>
              The materials appearing on DocGen's website could include technical, typographical, or photographic errors. DocGen does not warrant that any of the materials on its website are accurate, complete, or current. DocGen may make changes to the materials contained on its website at any time without notice.
            </p>
            
            <h2 className={`text-xl font-semibold mt-6 mb-3 ${theme === 'dark' ? 'text-white' : 'text-gray-800'}`}>7. Links</h2>
            <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'} mb-4`}>
              DocGen has not reviewed all of the sites linked to its website and is not responsible for the contents of any such linked site. The inclusion of any link does not imply endorsement by DocGen of the site. Use of any such linked website is at the user's own risk.
            </p>
            
            <h2 className={`text-xl font-semibold mt-6 mb-3 ${theme === 'dark' ? 'text-white' : 'text-gray-800'}`}>8. Modifications</h2>
            <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'} mb-4`}>
              DocGen may revise these terms of use for its website at any time without notice. By using this website, you are agreeing to be bound by the then current version of these terms of service.
            </p>
            
            <h2 className={`text-xl font-semibold mt-6 mb-3 ${theme === 'dark' ? 'text-white' : 'text-gray-800'}`}>9. Governing Law</h2>
            <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'} mb-4`}>
              These terms and conditions are governed by and construed in accordance with the laws of the United States and you irrevocably submit to the exclusive jurisdiction of the courts in that location.
            </p>
            
            <h2 className={`text-xl font-semibold mt-6 mb-3 ${theme === 'dark' ? 'text-white' : 'text-gray-800'}`}>10. Contact Us</h2>
            <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'} mb-4`}>
              If you have any questions about these Terms of Use, please contact us at <a href="mailto:aniket@docgen.dev" className={`${theme === 'dark' ? 'text-primary' : 'text-primary-light'} hover:underline`}>aniket@docgen.dev</a>.
            </p>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default TermsOfUse; 